const URL_KEYS = new Set(["url", "href"]);
const JS_SCHEME = String.fromCharCode(106, 97, 118, 97, 115, 99, 114, 105, 112, 116, 58);
const DATA_SCHEME = "data:";

export function getAllowedOrigins() {
  const configured = process.env.REACT_APP_VEGA_ALLOWED_DATA_ORIGINS || "";
  return new Set(
    configured
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
  );
}

function isAbsoluteOrProtocolRelativeUrl(value) {
  return /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(value) || value.startsWith("//");
}

export function validateSpecUrls(node, allowedOrigins, currentOrigin, path = "$", seen = new Set()) {
  if (node === null || node === undefined) return null;
  if (typeof node !== "object") return null;
  if (seen.has(node)) return null;
  seen.add(node);

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const issue = validateSpecUrls(node[i], allowedOrigins, currentOrigin, `${path}[${i}]`, seen);
      if (issue) return issue;
    }
    return null;
  }

  for (const [key, value] of Object.entries(node)) {
    const keyPath = `${path}.${key}`;
    if (URL_KEYS.has(key) && typeof value === "string") {
      const candidate = value.trim();
      const lower = candidate.toLowerCase();

      if (lower.slice(0, JS_SCHEME.length) === JS_SCHEME || lower.startsWith(DATA_SCHEME)) {
        return `${keyPath} contains a blocked URL scheme`;
      }

      if (isAbsoluteOrProtocolRelativeUrl(candidate)) {
        let parsed;
        try {
          parsed = new URL(candidate, currentOrigin);
        } catch (_err) {
          return `${keyPath} contains an invalid URL`;
        }

        if (parsed.origin !== currentOrigin && !allowedOrigins.has(parsed.origin)) {
          return `${keyPath} points to non-whitelisted origin: ${parsed.origin}`;
        }
      }
    }

    const nestedIssue = validateSpecUrls(value, allowedOrigins, currentOrigin, keyPath, seen);
    if (nestedIssue) return nestedIssue;
  }

  return null;
}

function normalizeMarkType(mark) {
  if (!mark) return "";
  if (typeof mark === "string") return mark.toLowerCase();
  if (typeof mark === "object" && typeof mark.type === "string") {
    return mark.type.toLowerCase();
  }
  return "";
}

function findFirstDiscreteYField(candidateSpec) {
  const y = candidateSpec?.encoding?.y;
  if (y && typeof y === "object") {
    const yType = String(y.type || "").toLowerCase();
    if ((yType === "nominal" || yType === "ordinal") && typeof y.field === "string") {
      return y.field;
    }
  }

  if (Array.isArray(candidateSpec?.layer)) {
    for (const layer of candidateSpec.layer) {
      const field = findFirstDiscreteYField(layer);
      if (field) return field;
    }
  }

  return null;
}

function hasTextHeavyLayers(candidateSpec) {
  if (!candidateSpec || typeof candidateSpec !== "object") return false;
  if (normalizeMarkType(candidateSpec.mark) === "text") return true;
  if (!Array.isArray(candidateSpec.layer)) return false;
  return candidateSpec.layer.some((layer) => normalizeMarkType(layer?.mark) === "text");
}

function getDataValues(candidateSpec) {
  return Array.isArray(candidateSpec?.data?.values) ? candidateSpec.data.values : null;
}

function getLongestStringLength(values) {
  if (!Array.isArray(values)) return 0;
  let longest = 0;
  for (const row of values) {
    if (!row || typeof row !== "object") continue;
    for (const value of Object.values(row)) {
      if (typeof value === "string") {
        longest = Math.max(longest, value.trim().length);
      }
    }
  }
  return longest;
}

function shouldPruneTextLayers(candidateSpec) {
  if (!Array.isArray(candidateSpec?.layer) || !hasTextHeavyLayers(candidateSpec)) {
    return false;
  }
  const values = getDataValues(candidateSpec);
  if (!values || values.length < 20) return false;
  const longestString = getLongestStringLength(values);
  return longestString >= 30;
}

function pruneDenseTextLayers(candidateSpec) {
  if (!shouldPruneTextLayers(candidateSpec)) return candidateSpec;

  const nonTextLayers = candidateSpec.layer.filter(
    (layer) => normalizeMarkType(layer?.mark) !== "text"
  );

  if (nonTextLayers.length === 0) return candidateSpec;

  return { ...candidateSpec, layer: nonTextLayers };
}

/**
 * Remove $schema from a Vega-Lite spec so vega-embed uses the installed
 * library version without emitting a version-mismatch warning.
 * Also converts `tooltip: true` in encoding channels to `{content: "data"}`,
 * which is the Vega-Lite v6 equivalent of "show all data fields as tooltip".
 */
function sanitizeSpec(spec) {
  if (!spec || typeof spec !== "object") return spec;

  // Strip version-pinning schema declaration — vega-embed v6 warns when the
  // spec declares v5 but the installed library is v6.
  // eslint-disable-next-line no-unused-vars
  const { $schema, ...rest } = spec;

  // Normalise encoding.tooltip: true → {content: "data"} (v6 format).
  if (rest.encoding && typeof rest.encoding === "object") {
    if (rest.encoding.tooltip === true) {
      rest.encoding = { ...rest.encoding, tooltip: { content: "data" } };
    }
  }

  // Recurse into composite view specs (layer, concat, hconcat, vconcat, spec).
  for (const key of ["layer", "concat", "hconcat", "vconcat"]) {
    if (Array.isArray(rest[key])) {
      rest[key] = rest[key].map(sanitizeSpec);
    }
  }
  if (rest.spec && typeof rest.spec === "object") {
    rest.spec = sanitizeSpec(rest.spec);
  }

  return rest;
}

export function applyReadabilityDefaults(candidateSpec) {
  const prunedSpec = pruneDenseTextLayers(sanitizeSpec(candidateSpec));
  const next = {
    ...prunedSpec,
    config: {
      ...(prunedSpec?.config || {}),
      axis: {
        labelLimit: 140,
        labelOverlap: "greedy",
        ...(prunedSpec?.config?.axis || {}),
      },
      axisY: {
        labelLimit: 220,
        ...(prunedSpec?.config?.axisY || {}),
      },
      text: {
        limit: 120,
        ...(prunedSpec?.config?.text || {}),
      },
    },
  };

  if (!hasTextHeavyLayers(prunedSpec)) {
    return next;
  }

  if (typeof next.width !== "number") {
    next.width = 900;
  } else {
    next.width = Math.max(next.width, 900);
  }

  const yField = findFirstDiscreteYField(prunedSpec);
  const values = getDataValues(prunedSpec);
  if (yField && values) {
    const rowCount = new Set(values.map((row) => row?.[yField]).filter(Boolean)).size;
    if (rowCount > 0) {
      const suggestedHeight = Math.min(Math.max(rowCount * 32, 360), 1400);
      if (typeof next.height !== "number" || next.height < suggestedHeight) {
        next.height = suggestedHeight;
      }
      return next;
    }
  }

  if (typeof next.height !== "number") {
    next.height = 500;
  } else {
    next.height = Math.max(next.height, 500);
  }

  return next;
}
